import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMap } from "react-leaflet";
import {
  QrAPI,
  FloorAPI,
  NavigationAPI,
  ShapeAPI,
  resolveAsset,
} from "../../../services/api";
import LeafletFloorMap from "../../../components/LeafletFloorMap";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/setqr/delete.svg";
import { ReactComponent as LocationIcon } from "../../../assets/icons/setqr/location.svg";
import { ReactComponent as ClickIcon } from "../../../assets/icons/setqr/click.svg";
import { ReactComponent as HandIcon } from "../../../assets/icons/setqr/hand.svg";
import { ReactComponent as EyeIcon } from "../../../assets/icons/setqr/eye.svg";
import { ReactComponent as QrIcon } from "../../../assets/icons/layout1/qrcode.svg";

function getFloorLabel(f, t) {
  const name = f.name || "";
  const level = f.level;
  if (!name) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  const lowerName = name.toLowerCase().trim();
  const isGeneric =
    lowerName === `floor${level}` ||
    lowerName === `floor ${level}` ||
    lowerName === `floor_${level}` ||
    lowerName === `ชั้น${level}` ||
    lowerName === `ชั้น ${level}` ||
    lowerName === `ชั้นที่${level}` ||
    lowerName === `ชั้นที่ ${level}`;
  if (isGeneric) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  return name;
}

const shapeBelongsToFloor = (shape, floorId) => {
  const shapeFloorId = shape.floorId ?? shape.floor_id ?? shape.floor?.id;
  return shapeFloorId == null || String(shapeFloorId) === String(floorId);
};

export default function SetQR() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const buildingId = searchParams.get("buildingId");
  const floorId = searchParams.get("floorId");

  const [floorOptions, setFloorOptions] = useState([]);
  const [floorData, setFloorData] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [points, setPoints] = useState([]);
  const [activePoint, setActivePoint] = useState(null); // id ของ QR ที่เลือก
  const [form, setForm] = useState({ name: "", code: "", x: "", y: "" });
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const mapRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [activeTool, setActiveTool] = useState("location"); // location = Set QR (blue in screenshot)

  const zoomPercent = Math.round(Math.pow(2, zoomLevel) * 100);

  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();

  function MapController() {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
      setZoomLevel(map.getZoom());
      const onZoom = () => setZoomLevel(map.getZoom());
      map.on("zoom", onZoom);
      return () => map.off("zoom", onZoom);
    }, [map]);
    return null;
  }

  // โหลด floors ของ building
  useEffect(() => {
    if (!buildingId) return;
    FloorAPI.listByBuilding(buildingId)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setFloorOptions(list);
        if (!floorId && list.length > 0) {
          setSearchParams(
            { buildingId, floorId: String(list[0].id) },
            { replace: true }
          );
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // โหลด floor data (รูปแผนผัง)
  useEffect(() => {
    if (!floorId) {
      setFloorData(null);
      return;
    }
    NavigationAPI.getFloor(floorId)
      .then((d) => setFloorData(d))
      .catch(() => setFloorData(null));
  }, [floorId]);

  // โหลด shapes (เพื่อแสดงเป็น context — ห้องที่ admin วาดไว้)
  useEffect(() => {
    if (!floorId) {
      setShapes([]);
      return;
    }
    setShapes([]);
    ShapeAPI.listByFloor(floorId)
      .then((d) =>
        setShapes(
          (Array.isArray(d) ? d : []).filter((shape) =>
            shapeBelongsToFloor(shape, floorId)
          )
        )
      )
      .catch(() => setShapes([]));
  }, [floorId]);

  // โหลด QR points
  const reload = () => {
    if (!floorId) {
      setPoints([]);
      return;
    }
    QrAPI.listByFloor(floorId)
      .then((data) => setPoints(Array.isArray(data) ? data : []))
      .catch(() => setPoints([]));
  };
  useEffect(reload, [floorId]);

  // เมื่อเลือก QR ที่มีอยู่ → fill form
  useEffect(() => {
    if (!activePoint) return;
    const p = points.find((x) => String(x.id) === String(activePoint));
    if (p) {
      setForm({
        name: p.name || "",
        code: p.code || "",
        x: String(p.x ?? ""),
        y: String(p.y ?? ""),
      });
    }
  }, [activePoint, points]);

  // ตอนเปลี่ยน floor → reset form/selection
  useEffect(() => {
    setActivePoint(null);
    setForm({ name: "", code: "", x: "", y: "" });
    setErrMsg("");
  }, [floorId]);

  // เมื่อคลิกบนแผนที่ → set พิกัดในฟอร์ม (เตรียมเพิ่ม QR ใหม่)
  const onMapClick = (px) => {
    if (activeTool !== "location") return;
    setActivePoint(null);
    setForm((f) => ({
      ...f,
      x: String(Math.round(px.x)),
      y: String(Math.round(px.y)),
    }));
  };

  // เปลี่ยน floor ผ่านปุ่มด้านบน
  const pickFloor = (id) => {
    setSearchParams(
      { ...(buildingId ? { buildingId } : {}), floorId: String(id) },
      { replace: true }
    );
  };

  // markers ที่จะแสดงบนแผนที่
  const markers = useMemo(
    () =>
      points.map((p) => ({
        id: p.id,
        type: "qr",
        x: Number(p.x) || 0,
        y: Number(p.y) || 0,
        label: p.name || p.code,
        onClick: () =>
          setActivePoint((prev) => (String(prev) === String(p.id) ? null : p.id)),
      })),
    [points]
  );

  const onSave = async () => {
    setErrMsg("");
    if (!floorId) {
      setErrMsg(t("admin.setQr.noFloorId", "ไม่พบ floorId ใน URL (เพิ่ม ?floorId=1)"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        code: form.code,
        x: Number(form.x) || 0,
        y: Number(form.y) || 0,
      };
      if (activePoint) {
        await QrAPI.update(activePoint, payload);
      } else {
        await QrAPI.create(floorId, payload);
      }
      setForm({ name: "", code: "", x: "", y: "" });
      setActivePoint(null);
      reload();
    } catch (e) {
      setErrMsg(e.message || t("admin.setQr.saveFailed", "บันทึกไม่สำเร็จ"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!activePoint) return;
    if (!window.confirm(t("admin.setQr.confirmDeletePrompt", "ลบคิวอาร์โค้ดนี้?"))) return;
    try {
      await QrAPI.remove(activePoint);
      setActivePoint(null);
      setForm({ name: "", code: "", x: "", y: "" });
      reload();
    } catch (e) {
      alert(e.message || t("admin.setQr.deleteFailed", "ลบไม่สำเร็จ"));
    }
  };

  const imageUrl = resolveAsset(floorData?.imageUrl);

  return (
    <div className="font-sans w-full h-full overflow-hidden">
      <div className="flex w-full h-full overflow-hidden">
        {/* LEFT: 70% map area */}
        <div className="w-[70%] min-w-0 flex flex-col">
          {/* Top bar: floor selector and tools */}
          <div className="flex items-center justify-between bg-white px-3 shrink-0 border-b border-gray-200" style={{ height: '72px' }}>
            {/* floor tabs */}
            <div className="flex items-center gap-1 overflow-x-auto mr-8">
              {floorOptions.map((f) => (
                <button
                  key={f.id}
                  onClick={() => pickFloor(f.id)}
                  className={`px-5 h-10 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${String(f.id) === String(floorId)
                    ? "bg-blue-500 text-white"
                    : "bg-white text-slate-500 hover:bg-gray-100"
                    }`}
                >
                  {getFloorLabel(f, t)}
                </button>
              ))}
              {floorOptions.length === 0 && (
                <span className="text-xs text-slate-400">
                  {t("admin.setQr.noFloorsWarning", "ยังไม่มีชั้น — กรุณาเพิ่มชั้นในหน้าตั้งค่าอาคารก่อน")}
                </span>
              )}
            </div>

            {/* tools — right */}
            <div className="flex items-center gap-0.5">

              {/* mouse */}
              <button
                title={t("admin.setQr.toolSelectEdit", "เลือก/แก้ไข")}
                onClick={() => setActiveTool("mouse")}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "mouse" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <ClickIcon className="w-4 h-4" />
              </button>

              {/* hand */}
              <button
                title={t("admin.setQr.toolPanDrag", "เลื่อน/ลาก")}
                onClick={() => setActiveTool("hand")}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "hand" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <HandIcon className="w-4 h-4" />
              </button>

              {/* eye */}
              <button
                title={t("admin.setQr.toolView", "ดูแผนที่")}
                onClick={() => setActiveTool("eye")}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "eye" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <EyeIcon className="w-4 h-4" />
              </button>

              {/* location (Set QR) */}
              <button
                title={t("admin.setQr.toolSetQr", "ตั้งค่าคิวอาร์โค้ด")}
                onClick={() => setActiveTool("location")}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "location" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <QrIcon className="w-4 h-4" />
              </button>

              {/* zoom + */}
              <button
                onClick={handleZoomIn}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>

              {/* zoom % */}
              <span className="text-sm text-slate-500 select-none min-w-[44px] text-center">
                {zoomPercent}%
              </span>

              {/* zoom − */}
              <button
                onClick={handleZoomOut}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14" />
                </svg>
              </button>

            </div>
          </div>

          {/* Map canvas */}
          <div className="relative flex-1 min-h-[500px] bg-slate-200">
            {imageUrl ? (
              <LeafletFloorMap
                imageUrl={imageUrl}
                imageWidth={floorData?.imageWidth}
                imageHeight={floorData?.imageHeight}
                shapes={shapes}
                markers={markers}
                onMapClick={onMapClick}
                className="w-full h-full"
              >
                <MapController />
              </LeafletFloorMap>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                {floorId ? t("admin.setQr.floorMapEmpty", "ไม่มีรูปแผนผังของชั้นนี้") : t("admin.setQr.selectFloorPrompt", "กรุณาเลือกชั้น")}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: 30% form area */}
        <div className="w-[30%] min-w-[320px] h-full flex flex-col border-l border-gray-200 bg-white">
          {/* Form content */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <div className="font-bold text-slate-900 border-b-2 border-slate-100 pb-5">
              {t("admin.setQr.formTitle", "แบบฟอร์มตั้งค่าคิวอาร์โค้ด")}
              {activePoint && (
                <span className="text-yellow-500 m-2 text-sm">
                  {t("admin.setQr.editActiveId", { defaultValue: "(แก้ไข ID: {{id}})", id: activePoint })}
                </span>
              )}
            </div>

            <div className="px-1 py-1">
              <label className="text-sm mt-5 block m-1 font-bold">
                {t("admin.setQr.qrNameLabel", "ชื่อคิวอาร์โค้ด")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder={t("admin.setQr.qrNamePlaceholder", "ทางเข้าใหญ่ A")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-10"
              />
            </div>

            <div className="px-1 py-1">
              <label className="text-sm mt-7 block m-1 font-bold">
                {t("admin.setQr.qrCodeLabel", "รหัส QR")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="QR-001"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-3 text-sm h-10"
              />
            </div>

            <label className="text-sm flex m-1 px-1 mt-8 font-bold">
              <LocationIcon className="text-blue-700 w-4 h-4 mt-0.5 mr-2" />{" "}
              {t("admin.setQr.qrMapCoordinatesLabel", "พิกัดคิวอาร์โค้ดบนแผนที่")}
            </label>
            <div className="flex px-1 py-0 gap-2">
              <input
                type="text"
                placeholder="x-coord"
                value={form.x}
                onChange={(e) => setForm({ ...form, x: e.target.value })}
                className="flex-1 min-w-0 border border-slate-300 rounded-md p-3 text-sm h-10"
              />
              <input
                type="text"
                placeholder="y-coord"
                value={form.y}
                onChange={(e) => setForm({ ...form, y: e.target.value })}
                className="flex-1 min-w-0 border border-slate-300 rounded-md p-3 text-sm h-10"
              />
            </div>

            <div className="px-1 py-1 mt-7">
              <button
                onClick={onDelete}
                disabled={!activePoint}
                className="w-full text-sm border items-center justify-center font-semibold flex border-red-100 bg-red-50 text-red-700 rounded-lg p-2 disabled:opacity-50"
              >
                <DeleteIcon className="text-red-700 mr-2 m-1" />{t("admin.setQr.deleteQr", "ลบคิวอาร์โค้ด")}
              </button>
            </div>
            {errMsg && (
              <div className="mx-2 mt-2 text-sm text-red-600">{errMsg}</div>
            )}
          </div>

          {/* Footer action buttons */}
          <div className="shrink-0">
            <div className="flex justify-center gap-2 m-2 mb-3">
              <button
                onClick={() => {
                  setActivePoint(null);
                  setForm({ name: "", code: "", x: "", y: "" });
                }}
                className="flex-1 py-2 bg-white text-slate-700 rounded-lg border border-gray-200 font-bold"
              >
                {t("admin.setQr.cancel", "ยกเลิก")}
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="flex-1 py-2 bg-yellow-500 text-white rounded-lg font-bold disabled:opacity-60"
              >
                {saving ? t("admin.setQr.saving", "กำลังบันทึก...") : activePoint ? t("admin.setQr.edit", "แก้ไข") : t("admin.setQr.add", "เพิ่ม")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
